import { EntityWithId } from '../interfaces';
import { UpdatesState } from './updatesReducer';

export enum Types {
  GET_REQUESTED = 'APIGatewayService:GetRequested',
  GET_STARTED = 'APIGatewayService:GetStarted',
  GET_COMPLETED = 'APIGatewayService:GetCompleted',
  GET_FAILED = 'APIGatewayService:GetFailed',

  GET_COLLECTION_REQUESTED = 'APIGatewayService:GetCollectionRequested',
  GET_COLLECTION_STARTED = 'APIGatewayService:GetCollectionStarted',
  GET_COLLECTION_COMPLETED = 'APIGatewayService:GetCollectionCompleted',
  GET_COLLECTION_FAILED = 'APIGatewayService:GetCollectionFailed',

  CREATE_REQUESTED = 'APIGatewayService:CreateRequested',
  CREATE_STARTED = 'APIGatewayService:CreateStarted',
  CREATE_COMPLETED = 'APIGatewayService:CreateCompleted',
  CREATE_FAILED = 'APIGatewayService:CreateFailed',

  DELETE_REQUESTED = 'APIGatewayService:DeleteRequested',
  DELETE_STARTED = 'APIGatewayService:DeleteStarted',
  DELETE_COMPLETED = 'APIGatewayService:DeleteCompleted',
  DELETE_FAILED = 'APIGatewayService:DeleteFailed',

  UPDATE_REQUESTED = 'APIGatewayService:UpdateRequested',
  UPDATE_STARTED = 'APIGatewayService:UpdateStarted',
  UPDATE_COMPLETED = 'APIGatewayService:UpdateCompleted',
  UPDATE_FAILED = 'APIGatewayService:UpdateFailed',

  PATCH_REQUESTED = 'APIGatewayService:PatchRequested',
  PATCH_STARTED = 'APIGatewayService:PatchStarted',
  PATCH_COMPLETED = 'APIGatewayService:PatchCompleted',
  PATCH_FAILED = 'APIGatewayService:PatchFailed',

  CREATE_LOCAL = 'APIGatewayService:CreateLocal',
  CREATE_LOCAL_WITH_TEMPORARY_ID = 'APIGatewayService:CreateLocalWithTemporaryId',
  UPDATE_LOCAL = 'APIGatewayService:UpdateLocal',
  DISCARD_LOCAL = 'APIGatewayService:DiscardLocal',
  DISCARD_ALL_LOCAL = 'APIGatewayService:DiscardAllLocal',

  CLEAR_ALL = 'APIGatewayService:ClearAll',

  RESTORE_UPDATES = 'APIGatewayService:RestoreUpdates',
}

interface Api<S> {
  service: S;
}

export interface RequestWithQuery<S> extends Api<S> {
  type: Types.GET_COLLECTION_REQUESTED;
  payload: {
    clear?: boolean;
    params?: { [key: string]: string };
    query?: { [key: string]: string };
  };
}
export interface RequestWithId<S> extends Api<S> {
  type: Types.DELETE_REQUESTED;
  payload: {
    id: string;
    params?: { [key: string]: string };
  };
}

export interface RequestWithIdAndQuery<S> extends Api<S> {
  type: Types.GET_REQUESTED;
  payload: {
    id: string;
    params?: { [key: string]: string };
    query?: { [key: string]: string };
  };
}

export interface RequestWithEntity<T, S> extends Api<S> {
  type: Types.UPDATE_REQUESTED | Types.CREATE_REQUESTED;
  payload: {
    id: string;
    entity: T;
    params?: { [key: string]: string };
  };
}
export interface RequestWithChanges<S> extends Api<S> {
  type: Types.PATCH_REQUESTED;
  payload: {
    id: string;
    changes: { [key: string]: string };
    params?: { [key: string]: string };
  };
}

interface Start<S> extends Api<S> {
  type: Types.GET_COLLECTION_STARTED;
}
interface StartWithId<S> extends Api<S> {
  type: Types.GET_STARTED | Types.CREATE_STARTED | Types.DELETE_STARTED | Types.UPDATE_STARTED | Types.PATCH_STARTED;
  payload: {
    id: string;
  };
}

interface CompleteWithId<S> extends Api<S> {
  type: Types.DELETE_COMPLETED;
  payload: {
    id: string;
  };
}
export interface CompleteWithEntity<T, S> extends Api<S> {
  type: Types.GET_COMPLETED | Types.UPDATE_COMPLETED | Types.PATCH_COMPLETED;
  payload: {
    id: string;
    entity: T;
  };
}
interface CompleteWithEntityAndTempId<T, S> extends Api<S> {
  type: Types.CREATE_COMPLETED;
  payload: {
    tempId: string;
    id: string;
    entity: T;
  };
}
interface CompleteWithEntityList<T, S> extends Api<S> {
  type: Types.GET_COLLECTION_COMPLETED;
  payload: {
    entities: EntityWithId<T>[];
  };
}

interface Fail<S> extends Api<S> {
  type: Types.GET_COLLECTION_FAILED;
  payload: {
    error: string;
  };
}
export interface FailWithId<S> extends Api<S> {
  type: Types.CREATE_FAILED | Types.GET_FAILED | Types.DELETE_FAILED | Types.UPDATE_FAILED | Types.PATCH_FAILED;
  payload: {
    id: string;
    error: string;
  };
}

interface CreateLocal<T, S> extends Api<S> {
  type: Types.CREATE_LOCAL;
  payload: {
    entity: T;
  };
}
interface CreateLocalWithTemporaryId<T, S> extends Api<S> {
  type: Types.CREATE_LOCAL_WITH_TEMPORARY_ID;
  payload: {
    entity: T;
    id: string;
  };
}
interface UpdateLocal<T, S> extends Api<S> {
  type: Types.UPDATE_LOCAL;
  payload: {
    id: string;
    entity: T;
  };
}
interface DiscardLocal<S> extends Api<S> {
  type: Types.DISCARD_LOCAL;
  payload: {
    id: string;
  };
}
interface DiscardAllLocal<S> extends Api<S> {
  type: Types.DISCARD_ALL_LOCAL;
}

interface ClearAll<S> extends Api<S> {
  type: Types.CLEAR_ALL;
}

interface RestoreUpdates<T, S> extends Api<S> {
  type: Types.RESTORE_UPDATES;
  payload: UpdatesState<T>;
}

export type Action<T, S> =
  | RequestWithId<S>
  | RequestWithQuery<S>
  | RequestWithIdAndQuery<S>
  | RequestWithEntity<T, S>
  | RequestWithEntity<T, S>
  | RequestWithChanges<S>
  | Start<S>
  | StartWithId<S>
  | CompleteWithId<S>
  | CompleteWithEntity<T, S>
  | CompleteWithEntityAndTempId<T, S>
  | CompleteWithEntityList<T, S>
  | Fail<S>
  | FailWithId<S>
  | CreateLocal<T, S>
  | CreateLocalWithTemporaryId<T, S>
  | UpdateLocal<T, S>
  | DiscardLocal<S>
  | DiscardAllLocal<S>
  | ClearAll<S>
  | RestoreUpdates<T, S>;
